import * as React from 'react';
import Slider from 'react-slick';
import loadable from '@loadable/component';
import classnames from 'classnames';

import Button from '@components/Button';
import PdfViewer from '@components/DocumentViewer/PdfViewer';
import XlsxViewer from '@components/DocumentViewer/XlsxViewer';
import ModalBase, { DeleteModal } from '@components/ModalBase';

import { getEmptyErrorState } from '@utils/AppUtil';
import {
  hasRegularUnits,
  IAttachmentTagContext,
  isTaggableImageFile,
  nextAttachmentTagState,
  storedAttachmentTagId,
} from '@utils/attachmentTag';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';
import FileUtil from '@utils/FileUtil';
import { track } from '@utils/MixPanelUtils';

import { DownloadButton } from '../DocumentViewer';

import AttachmentData from './AttachmentData';
import AttachmentDescription from './AttachmentDescription';
import AttachmentImage from './AttachmentImage';
import AttachmentTagField from './AttachmentTagField';

const DocumentViewer = loadable(
  () =>
    import(
      /* webpackChunkName: "document-viewer" */
      '@components/DocumentViewer/DocumentViewer'
    )
);
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';

import './style.scss';

export interface IDisplayEditAttachment extends IAttachment {
  error?: {
    title: IFieldValidation;
    description: IFieldValidation;
  };
  index?: number;
  createdAt?: string;
  createdBy?: ICaseUserResponse;
  exif?: IAttachmentExif;
  selectedLocationUnitId?: number | null;
  originalLocationUnitId?: number | null;
}

export interface IAttachmentsEditModalProps {
  show: boolean;
  onClose: (e: React.SyntheticEvent<HTMLElement>) => void;
  onUpdateAttachments?: (attachments: IAttachment[]) => void;
  attachments: IDisplayEditAttachment[];
  actionButtons?: JSX.Element[];
  openOnIndex?: number;
  onDeleteImage?: (attachmentIndex: number[], closeAttachmentEditModal?: boolean) => void;
  updateOpenOnIndex?: (attachmentIndex: number) => void;
  title: string;
  attachmentMode: TEditAttachmentModes;
  trackLabel?: string;
  className?: string;
  tagContext?: IAttachmentTagContext;
}

interface IAttachmentsEditModalState {
  attachmentDeleteIndex: number;
  currentSlide: number;
  modifiedAttachments: IDisplayEditAttachment[];
  photoAttachments: IDisplayEditAttachment[];
  isDeleteModalOpen: boolean;
  imageToBeDeletedIndex: number;
  documentView: boolean;
}

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGen = new ErrorMessageGenerator();

export default class AttachmentsEditModal extends React.Component<
  IAttachmentsEditModalProps,
  IAttachmentsEditModalState
> {
  slider = null;

  constructor(props: IAttachmentsEditModalProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidMount() {
    const modifiedAttachments = this.getModifiedAttachmentsFromProps(this.props);
    const photoAttachments = modifiedAttachments.filter((attachment) => this.getFileType(attachment) === 'image');
    let documentView = false;
    if (!isNaN(this.props.openOnIndex)) {
      const attachmentFile = this.props.attachments[this.props.openOnIndex];
      documentView = attachmentFile ? this.getFileType(attachmentFile) === 'others' : false;
    }
    this.setState({
      modifiedAttachments,
      photoAttachments,
      documentView,
    });
  }

  componentDidUpdate(prevProps: Readonly<IAttachmentsEditModalProps>) {
    if (this.props.attachments !== prevProps.attachments || this.props.openOnIndex !== prevProps.openOnIndex) {
      const attachmentFile = this.props.attachments[this.props.openOnIndex];
      const documentView = attachmentFile ? this.getFileType(attachmentFile) === 'others' : false;
      const modifiedAttachments = this.getModifiedAttachmentsFromProps(this.props);
      const photoAttachments = modifiedAttachments.filter((attachment) => this.getFileType(attachment) === 'image');

      this.setState({
        modifiedAttachments,
        photoAttachments,
        documentView,
      });

      if (this.props.openOnIndex !== prevProps.openOnIndex) {
        this.setState({
          documentView,
          currentSlide: this.props.openOnIndex || 0,
        });
      }
    }
  }

  getEmptyState = (): IAttachmentsEditModalState => ({
    attachmentDeleteIndex: null,
    currentSlide: 0,
    modifiedAttachments: [],
    isDeleteModalOpen: false,
    imageToBeDeletedIndex: null,
    documentView: false,
    photoAttachments: [],
  });

  toggleDeleteForm = (e: React.SyntheticEvent<MouseEvent>, imageToBeDeletedIndex: number = null) => {
    this.setState((prevState) => ({
      isDeleteModalOpen: !prevState.isDeleteModalOpen,
      imageToBeDeletedIndex,
    }));
  };

  onDeleteRequest = () => {
    const imageIndex = [this.state.imageToBeDeletedIndex];
    const isDocument = this.state.documentView;
    if (this.state.imageToBeDeletedIndex !== null) {
      this.setState(
        {
          imageToBeDeletedIndex: null,
          isDeleteModalOpen: false,
          currentSlide: this.getNextSlideIndex(this.state.imageToBeDeletedIndex),
        },
        () => {
          if (this.slider && !isDocument && this.state.currentSlide !== null) {
            // this.slider.slickGoTo(this.state.currentSlide);
            if (this.props.updateOpenOnIndex) {
              this.props.updateOpenOnIndex(this.state.currentSlide);
            }
          }
          // To give some time for warning modal to close
          const closeModal = isDocument || this.state.currentSlide === null;
          setTimeout(() => this.props.onDeleteImage(imageIndex, closeModal), 0);
        }
      );
    }
  };

  getNextSlideIndex = (startIndex: number) => {
    const { modifiedAttachments } = this.state;
    const nextPhoto = modifiedAttachments
      .filter((a) => a.index > startIndex)
      .filter((a) => this.getFileType(a) === 'image')[0];

    const prevPhoto = modifiedAttachments
      .filter((a) => a.index < startIndex)
      .filter((a) => this.getFileType(a) === 'image')[0];

    return nextPhoto ? nextPhoto.index - 1 : prevPhoto ? prevPhoto.index : null;
  };

  getModifiedAttachmentsFromProps = (nextProps: IAttachmentsEditModalProps): IDisplayEditAttachment[] => {
    const { attachments } = nextProps;
    const trackTags = !!nextProps.tagContext && hasRegularUnits(nextProps.tagContext.units);

    return attachments.map((attachment, index) => {
      const next: IDisplayEditAttachment = {
        ...attachment,
        error: this.getFileEmptyErrorState(),
        index,
      };

      if (!trackTags || !isTaggableImageFile(attachment)) {
        return next;
      }

      const storedId = storedAttachmentTagId(attachment);

      return {
        ...next,
        originalLocationUnitId: storedId,
        selectedLocationUnitId: storedId,
        tagChanged: false,
        locationUnitId: storedId,
      };
    });
  };

  onClose = (e) => {
    this.props.onClose(e);
  };

  onChange = (e: React.SyntheticEvent<HTMLInputElement>, attachmentIndex: number) => {
    const targetName = e.currentTarget.name;
    const targetValue = e.currentTarget.value;

    this.setState((prevState) => {
      const modifiedAttachments = [...prevState.modifiedAttachments];
      const attachment = modifiedAttachments[attachmentIndex];

      let updateObj;
      if (targetName === 'createdAt' || targetName === 'location') {
        updateObj = {
          ...attachment,
          exif: {
            ...attachment.exif,
            [targetName]: targetValue || null,
          },
        };
      } else {
        updateObj = {
          ...attachment,
          [targetName]: targetValue,
        };
      }
      modifiedAttachments.splice(attachmentIndex, 1, updateObj);
      const photoAttachments = modifiedAttachments.filter((a) => this.getFileType(a) === 'image');

      return {
        modifiedAttachments,
        photoAttachments,
      };
    });
  };

  isTagFieldVisible = (attachment: IDisplayEditAttachment) =>
    !!this.props.tagContext && hasRegularUnits(this.props.tagContext.units) && isTaggableImageFile(attachment);

  onTagChange = (attachmentIndex: number, locationUnitId: number | null) => {
    if (typeof this.props.tagContext?.inspectionUnitId === 'number' && locationUnitId == null) {
      return;
    }

    this.setState((prevState) => {
      const modifiedAttachments = [...prevState.modifiedAttachments];
      const attachment = modifiedAttachments[attachmentIndex];

      modifiedAttachments.splice(attachmentIndex, 1, {
        ...attachment,
        ...nextAttachmentTagState(attachment, locationUnitId, this.props.tagContext?.units || []),
      });

      return {
        modifiedAttachments,
        photoAttachments: modifiedAttachments.filter((item) => this.getFileType(item) === 'image'),
      };
    });
  };

  getFileType = (file: IXerceCaseAttachmentRequest) => {
    const fileExtension: string = FileUtil.getFileExtension(file.fileURL);

    // TODO: this function should handle all file types, currently
    // only images is checked for.
    return /(jpe?g|png)$/.test(fileExtension) ? 'image' : 'others';
  };

  getThumb = (file: IXerceCaseAttachmentRequest) => {
    const defaultClassName = 'attachments-edit__saved-thumb';

    const fileTypeToThumb = {
      image: (
        <div className={classnames('flex-row--center', defaultClassName)}>
          <AttachmentImage src={file.data || file.fileURL} alt={file.fileName || 'Attachment preview'} />
        </div>
      ),
      others: <div className={`${defaultClassName} ${defaultClassName}--others`} />,
    };

    return fileTypeToThumb[this.getFileType(file)];
  };

  getDocumentPreview = (file: IXerceCaseAttachmentRequest) => {
    const fileNameExtension = FileUtil.getFileExtension(file.fileURL);

    if (fileNameExtension === 'pdf') {
      return <PdfViewer attachment={file} />;
    }

    if (fileNameExtension === 'xls' || fileNameExtension === 'xlsx') {
      return <XlsxViewer attachment={file} />;
    }

    return <DocumentViewer attachment={file} />;
  };

  getFileEmptyErrorState = () => ({
    title: getEmptyErrorState(),
    description: getEmptyErrorState(),
  });

  isValidFile = (file: IAttachment, fileName: string) => {
    const { description, title } = file;
    const error = this.getFileEmptyErrorState();
    let isValid = true;

    if (cyberdyneValidator.isStringGreaterThanLength(description, 300)) {
      error.description.errorState = IValidationState.ERROR;
      error.description.errorMessage = errMsgGen.getCharacterLimitMessage('Description', 300);

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(title)) {
      error.title.errorState = IValidationState.ERROR;
      error.title.errorMessage = errMsgGen.getMandatoryFieldMessage('Name');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(title, 200)) {
      error.title.errorState = IValidationState.ERROR;
      error.title.errorMessage = errMsgGen.getCharacterLimitMessage('Name', 200);

      isValid = false;
    }

    return { isValid, error };
  };

  isValid = () => {
    let isValid = true;
    const files = this.state.modifiedAttachments;

    const newFiles = files.map((file) => {
      const isFileValid = this.isValidFile(file, file.fileName);
      isValid = isValid && isFileValid.isValid;

      return {
        ...file,
        error: isFileValid.error,
      };
    });

    const photoAttachments = newFiles.filter((attachment) => this.getFileType(attachment) === 'image');
    this.setState({
      modifiedAttachments: newFiles,
      photoAttachments,
    });

    return isValid;
  };

  updateAttachments = (e) => {
    if (this.isValid()) {
      const updatedAttachments = this.state.modifiedAttachments.map((file) => ({
        ...file,
        description: file.description && file.description.trim() ? file.description.trim() : null,
      }));
      this.props.onUpdateAttachments(updatedAttachments);
    }
  };

  trackName = () => {
    track(`${this.props.trackLabel}_EDIT_ATTACHMENT_PHOTO_NAME_UPDATED`, {
      'Device Details': getFormattedDeviceDetails(),
    });
  };

  trackDescription = () => {
    track(`${this.props.trackLabel}_EDIT_ATTACHMENT_PHOTO_DESCRIPTION_UPDATED`, {
      'Device Details': getFormattedDeviceDetails(),
    });
  };

  readonlyModeFooter = () => <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onClose} content="Close" />;

  deleteModeFooter = () => <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onClose} content="Close" />;

  editModeFooter = () => (
    <div className={classnames('attachments-edit__footer')}>
      <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onClose} content="Cancel" />
      <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.updateAttachments} content="Update" />
    </div>
  );

  getFooter = (mode) => {
    const attachmentModalFooter = {
      [TEditAttachmentModes.DELETE]: this.deleteModeFooter,
      [TEditAttachmentModes.FULL_EDIT]: this.editModeFooter,
      [TEditAttachmentModes.READ_ONLY]: this.readonlyModeFooter,
    };

    return attachmentModalFooter[mode]();
  };

  renderDeleteModal = () => {
    const disclaimerText = 'Are you sure you want to delete this attachment?';

    const deleteModalFooter = (
      <div className={classnames('flex-row--middle')}>
        <Button onClick={this.toggleDeleteForm} bsStyle={ButtonStyle.DEFAULT}>
          Cancel
        </Button>
        <Button onClick={this.onDeleteRequest} bsStyle={ButtonStyle.DANGER}>
          Yes, Delete
        </Button>
      </div>
    );

    return (
      <DeleteModal
        key={2}
        show={this.state.isDeleteModalOpen}
        footer={deleteModalFooter}
        disclaimerText={disclaimerText}
      />
    );
  };

  renderDeleteOnlyMode = (attachment, index) => (
    <div className={classnames('attachments-edit__saved-details')}>
      <AttachmentData
        attachment={attachment}
        index={index}
        mode={TEditAttachmentModes.DELETE}
        deleteAction={this.toggleDeleteForm}
      />
    </div>
  );

  renderFullEditMode = (attachment, index) => {
    const actionButtonsFromProps = this.props.actionButtons || [];

    return (
      <div className={classnames('attachments-edit__saved-details')}>
        <AttachmentData
          attachment={attachment}
          index={index}
          actionButtons={actionButtonsFromProps}
          mode={TEditAttachmentModes.FULL_EDIT}
          deleteAction={this.toggleDeleteForm}
        />
        <AttachmentDescription
          attachment={attachment}
          mode={TEditAttachmentModes.FULL_EDIT}
          onChange={this.onChange}
          track={this.trackName}
          index={index}
          trackLabel={this.props.trackLabel}
        />
        {this.isTagFieldVisible(attachment) && this.props.tagContext && (
          <AttachmentTagField
            context={this.props.tagContext}
            value={attachment.selectedLocationUnitId ?? null}
            onChange={(locationUnitId) => this.onTagChange(index, locationUnitId)}
          />
        )}
      </div>
    );
  };

  renderReadOnlyMode = (attachment, index) => (
    <div className={classnames('attachments-edit__saved-details', 'read-only')}>
      <AttachmentData attachment={attachment} mode={TEditAttachmentModes.READ_ONLY} index={index} />
      <AttachmentDescription attachment={attachment} mode={TEditAttachmentModes.READ_ONLY} index={index} />
    </div>
  );

  renderAttachment = (mode) => {
    const attachmentModes = {
      [TEditAttachmentModes.DELETE]: this.renderDeleteOnlyMode,
      [TEditAttachmentModes.READ_ONLY]: this.renderReadOnlyMode,
      [TEditAttachmentModes.FULL_EDIT]: this.renderFullEditMode,
    };
    const renderAttachmentByMode = attachmentModes[mode];

    return (attachment) => (
      <div key={attachment.fileName} className={classnames('attachments-edit__carousel')}>
        {this.state.documentView ? this.getDocumentPreview(attachment) : this.getThumb(attachment)}
        {renderAttachmentByMode(attachment, attachment.index)}
      </div>
    );
  };

  renderBody = () => {
    const { documentView } = this.state;
    const { openOnIndex } = this.props;

    return documentView ? this.renderDocumentViewer(openOnIndex) : this.renderSlider();
  };

  renderSlider = () => {
    const sliderSettings = {
      dots: false,
      centerMode: true,
      infinite: false,
      vertical: false,
      speed: 500,
      slidesToShow: 1.1,
      slidesToScroll: 1,
      // tslint:disable-next-line: max-line-length
      initialSlide: this.state.photoAttachments.findIndex((a) => a.index === this.props.openOnIndex),
      afterChange: (currentSlide) => this.setState({ currentSlide: Math.ceil(currentSlide) }),
    };

    const viewingMessage = `Viewing ${this.state.currentSlide + 1} of ${this.props.attachments.length}`;

    return (
      <div className={classnames('attachments-edit__body')}>
        <label>{viewingMessage}</label>
        <Slider {...sliderSettings} ref={(slider) => (this.slider = slider)}>
          {this.state.photoAttachments.map(this.renderAttachment(this.props.attachmentMode))}
        </Slider>
      </div>
    );
  };

  renderDocumentViewer = (attachmentIndex: number) => {
    const document = this.state.modifiedAttachments[attachmentIndex];

    if (!document) {
      return null;
    }

    const viewingMessage = `Viewing ${document.fileName}`;

    return (
      <div className={classnames('attachments-edit__body document')}>
        <div className={'attachment-info'}>
          <div>
            <label>{viewingMessage}</label>
          </div>
          <DownloadButton attachment={document} />
        </div>
        {this.renderAttachment(this.props.attachmentMode)(document)}
      </div>
    );
  };

  render() {
    const title = `${this.props.title} Details`;
    const className = this.props.className || '';
    const { documentView } = this.state;

    return (
      <div>
        <ModalBase
          key={1}
          show={this.props.show}
          onClose={this.onClose}
          titleElement={title}
          bodyElement={this.renderBody()}
          footerElement={this.getFooter(this.props.attachmentMode)}
          className={classnames(
            {
              'document-viewer-modal': documentView,
            },
            'attachments-edit',
            className
          )}
        />
        {this.renderDeleteModal()}
      </div>
    );
  }
}
